/**
 * Mapbox GL JS v3 전용 API 를 MapLibre 위에서 흉내내는 얇은 층.
 *
 * **왜 필요한가.** 이 앱은 태양 위치에서 ambient/directional 조명을 계산해
 * `map.setLights()` 로 넣고, 나중에 **평가된 값을 되읽어** three.js 씬 조명과
 * deck.gl LightingEffect, 그리고 fog 색을 구동한다. MapLibre 에는 v3 의
 * lights 배열 API(`getLights`/`setLights`)와 `style.ambientLight` /
 * `style.directionalLight` 가 없다. 그래서 값을 여기에 들고 있는다.
 *
 * 스타일 자체의 음영(fill-extrusion 등)은 MapLibre 의 v1-스펙 `setLight()` 로
 * 근사해 같이 밀어준다. 그쪽은 단일 광원이라 directional 만 반영된다.
 *
 * # ponytail: 전이는 스냅이다. Mapbox 는 lights 를 style transition 으로
 * 보간해서 되읽으면 중간값이 나오는데, 여기서는 목표값이 즉시 보인다.
 * 낮/밤이 바뀌는 순간 열차 조명이 한 프레임에 튄다. 부드럽게 하려면 여기에
 * 시간 기반 보간을 넣으면 되고, 그 전까지는 화면에서 티가 나는지 먼저 본다.
 */

const DEFAULT_LIGHTS = [{
    id: 'ambient',
    type: 'ambient',
    properties: {color: 'rgb(255, 255, 255)', intensity: 0.5}
}, {
    id: 'directional',
    type: 'directional',
    properties: {
        direction: ['literal', [180, 30]],
        color: 'rgb(255, 255, 255)',
        intensity: 0.5,
        'cast-shadows': true,
        'shadow-intensity': 1
    }
}];

function clone(lights) {
    return lights.map(light => ({
        id: light.id,
        type: light.type,
        properties: Object.assign({}, light.properties)
    }));
}

/**
 * Mapbox v3 의 `map.getLights()` 대응. 항상 복사본을 준다 - 호출부가
 * 받아서 고친 뒤 setLights 로 되돌리는 패턴을 쓰기 때문이다.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @returns {Array<Object>} lights
 */
export function getLights(map) {
    return clone(map.__mt3dLights || DEFAULT_LIGHTS);
}

/**
 * Mapbox v3 의 `map.setLights()` 대응. 값을 보관하고, 스타일 음영용으로
 * MapLibre 의 단일 광원에도 밀어넣는다.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @param {Array<Object>} lights - lights
 */
export function setLights(map, lights) {
    map.__mt3dLights = clone(lights);

    const directional = map.__mt3dLights.filter(({type}) => type === 'directional')[0];

    if (!directional || !map.setLight) {
        return;
    }

    // MapLibre 의 light 는 v1 스펙이다: position 이 [radial, azimuthal, polar] 이고
    // azimuthal 은 북 기준 시계방향, polar 는 0=바로 위. 우리 direction 과 각도
    // 규약이 같아서 반지름만 얹으면 된다.
    const direction = directional.properties.direction,
        [azimuthal, polar] = Array.isArray(direction) && direction[0] === 'literal' ?
            direction[1] : direction;

    map.setLight({
        anchor: 'map',
        position: [1.5, azimuthal, polar],
        color: directional.properties.color,
        intensity: directional.properties.intensity
    });
}

/**
 * Mapbox 의 `map.style.getOwnLayer(id)` 대응. 평가된 paint 를 `.get()` 으로
 * 주는 Mapbox 내부 객체와 달리 MapLibre 는 `getLayer` 가 스타일 레이어를
 * 그대로 준다. 우리가 쓰는 건 `.metadata` 쓰기와 `.paint` 읽기 두 곳뿐이다.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @param {string} id - layer id
 * @returns {Object} style layer
 */
export function getOwnLayer(map, id) {
    return (map.style && map.style._layers && map.style._layers[id]) || map.getLayer(id);
}

/**
 * Mapbox 의 `map.getFreeCameraOptions().position.z` 대응 - 카메라 고도를
 * 머케이터 단위(1 = 적도 한 바퀴)로 준다. MapLibre 에는 FreeCamera API 가 없다.
 *
 * 유도: `cameraToCenterDistance` 는 화면 픽셀 단위의 시선 거리이므로 pitch 의
 * 코사인을 곱하면 중심점 위 수직 높이가 되고, `worldSize`(= 타일크기 × 2^zoom)로
 * 나누면 머케이터 단위가 된다. Mapbox 내부 공식과 같은 형태다.
 *
 * 검증(2026-09-07, zoom 11.5 / pitch 60): 이 식 4.0461e-4 vs MapLibre
 * `getCameraAltitude()` 를 미터에서 환산한 값 4.0416e-4 - 비 1.0011.
 * 0.11% 차이는 지형 고도 처리 차이이고, 이 값의 소비처(LOD 문턱·안개 거리)에는
 * 영향이 없다.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @returns {number} 머케이터 단위 카메라 고도
 */
export function getCameraMercatorZ(map) {
    const {cameraToCenterDistance, pitch, worldSize} = map.transform;

    return cameraToCenterDistance * Math.cos(pitch * Math.PI / 180) / worldSize;
}

// Mapbox 의 `style.getBrightness()`(= 스타일 표현식 ['measure-light','brightness'])
// 대응. MapLibre 에는 없고 공식도 공개돼 있지 않아 **소비처에 맞춰 눈금을 잡았다.**
//
// 유일한 소비처는 getFogColor 의 색 램프이고 그 스톱이 기대하는 범위가
// 0.01(밤) ~ 0.4(낮) 이다. 이 앱이 낮/밤을 실제로 움직이는 값은 ambient 라
// (낮 rgb(255,255,255) i=.7 / 밤 rgb(0,22,56) i=.5) 거기에 상수 하나를 곱해
// 양 끝을 맞춘다:
//   낮  1.000 × .7 × .55 = 0.385  (스톱 0.4)
//   밤  0.078 × .5 × .55 = 0.021  (스톱 0.01)
// directional 은 밤에도 회색 74 로 남아 낮/밤을 구분하지 못하므로 뺐다.
//
// # ponytail: 상수 .55 는 유도값이 아니라 보정값이다. Mapbox 원식이 공개되거나
// 안개가 눈에 띄게 어긋나면 여기만 고치면 된다.
const BRIGHTNESS_SCALE = 0.55;

/**
 * Mapbox 의 `map.style.getBrightness()` 대응.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @returns {number} 0-1 밝기
 */
export function getBrightness(map) {
    const ambient = getLights(map).filter(({type}) => type === 'ambient')[0];

    if (!ambient) {
        return 0;
    }

    const match = /(\d+)\D+(\d+)\D+(\d+)/.exec(ambient.properties.color);

    if (!match) {
        return 0;
    }

    const [r, g, b] = match.slice(1).map(Number),
        luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    return Math.min(Math.max(luminance * ambient.properties.intensity * BRIGHTNESS_SCALE, 0), 1);
}

/**
 * deck.gl `MapboxLayer` 를 꺼낸다. Mapbox 는 `getLayer(id)` 가 우리가 넣은
 * 커스텀 레이어 객체를 그대로 돌려주지만, MapLibre 는 `CustomStyleLayer` 로
 * 감싸고 원본을 `.implementation` 아래 둔다. 그래서 `.setProps` / `.props` 가
 * 한 겹 안으로 들어간다.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @param {string} id - layer id
 * @returns {Object} deck.gl MapboxLayer
 */
export function getDeckLayer(map, id) {
    const layer = map.getLayer(id);

    return (layer && layer.implementation) || layer;
}

// Mapbox 스타일의 기본 전이 시간. MapLibre 의 Style 에는 `transition` 이 없고
// 스타일이 선언했을 때만 `stylesheet.transition` 에 들어온다.
const DEFAULT_TRANSITION_DURATION = 300;

/**
 * Mapbox 의 `map.style.transition.duration` 대응.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @returns {number} 전이 시간(ms)
 */
export function getTransitionDuration(map) {
    const stylesheet = map.style && map.style.stylesheet,
        transition = stylesheet && stylesheet.transition;

    return (transition && transition.duration) || DEFAULT_TRANSITION_DURATION;
}

/**
 * deck.gl `@deck.gl/mapbox` 가 뷰포트를 만들 때 `map.transform._nearZ` /
 * `_farZ` 를 읽는다 (mapbox-gl 내부 필드). MapLibre 는 같은 값을 밑줄 없는
 * getter 로 노출하므로 없으면 `undefined / height` = **NaN** 이 되어
 * deck 레이어(지연 마커)가 조용히 안 그려진다.
 *
 * transform 은 투영이 바뀌면 새 객체로 갈리므로 프로토타입에 얹는다.
 * @param {maplibregl.Map} map - MapLibre's Map object
 */
export function patchDeckTransform(map) {
    const proto = Object.getPrototypeOf(map.transform);

    if (!proto || '_nearZ' in proto) {
        return;
    }
    Object.defineProperty(proto, '_nearZ', {get() {
        return this.nearZ;
    }});
    Object.defineProperty(proto, '_farZ', {get() {
        return this.farZ;
    }});
}
